import { useCallback } from "react";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import {
  basketUpgradesService,
  ItemTypeMappings,
} from "@shared/modules/basket/domain/services/BasketUpgradesService";
import { enumsTypes } from "@shared/constants";

interface UseBasketUpgradesProps {
  itemGroups: TicketItemGroupType[];
  itemTypeMappings: ItemTypeMappings;
  isEnabled: boolean;
  enableDataLoading: () => void;
  currency: enumsTypes.CurrencyMapIdType;
}

/**
 * Hook for basket upgrades functionality
 * Works with tickets, memberships, and products that have upgrade options
 */
export function useBasketUpgrades({
  itemGroups,
  itemTypeMappings,
  isEnabled,
  enableDataLoading,
  currency,
}: UseBasketUpgradesProps) {
  /**
   * Get basket upgrades for all items
   * Automatically enables data loading if not already enabled
   */
  const getBasketUpgrades = useCallback(() => {
    if (!itemGroups || !itemTypeMappings || !currency) {
      return undefined;
    }
    if (!isEnabled) {
      enableDataLoading();
    }
    return basketUpgradesService.getBasketUpgrades(
      itemGroups,
      itemTypeMappings,
      currency
    );
  }, [isEnabled, itemGroups, itemTypeMappings, enableDataLoading, currency]);

  /**
   * Get basket upgrade for a specific item group by ID
   * Automatically enables data loading if not already enabled
   */
  const getBasketUpgradeForItemGroupId = useCallback(
    (itemGroupId: number) => {
      if (!itemGroups || !itemTypeMappings || !currency) {
        return undefined;
      }
      if (!isEnabled) {
        enableDataLoading();
      }
      return basketUpgradesService.getUpgradeForItemGroupById(
        itemGroupId,
        itemGroups,
        itemTypeMappings,
        currency
      );
    },
    [isEnabled, itemGroups, itemTypeMappings, enableDataLoading, currency]
  );

  return {
    getBasketUpgrades,
    getBasketUpgradeForItemGroupId,
  };
}
